'use client';
import { useEffect, useState } from 'react';

import { getFeatureFlagClientSingleton } from '../model/base/Client';
import { type FEATURE_FLAGS_VALUE, getFeatureFlagDefault } from '../model/constants';

type UseFeatureFlagResult = {
  error?: string;
  isLoading: boolean;
  value: boolean;
};

export const useGetFeatureFlagBool = (flagKey: FEATURE_FLAGS_VALUE) => {
  const [result, setResult] = useState<UseFeatureFlagResult>(() => {
    const client = getFeatureFlagClientSingleton();

    if (client.isInitialized) {
      const flagResult = client.getCachedValue(flagKey);

      return {
        isLoading: false,
        value: flagResult ?? getFeatureFlagDefault(flagKey),
      };
    }

    return {
      isLoading: true,
      value: getFeatureFlagDefault(flagKey),
    };
  });

  useEffect(() => {
    const loadFlag = async () => {
      const featureFlagResult = await getFeatureFlagBool(flagKey);

      if (featureFlagResult.value !== result.value || featureFlagResult.error !== result.error) {
        setResult({
          error: featureFlagResult.error,
          isLoading: false,
          value: featureFlagResult.value,
        });
      }

      if (result.isLoading) {
        setResult((prev) => ({
          ...prev,
          isLoading: false,
        }));
      }
    };

    loadFlag();
  }, [flagKey, result.isLoading, result.value, result.error]);

  return result;
};

export const useGetFeatureFlagBoolValue = (flagKey: FEATURE_FLAGS_VALUE) => useGetFeatureFlagBool(flagKey).value;

export const getFeatureFlagBool = async (flagKey: FEATURE_FLAGS_VALUE) => {
  const client = getFeatureFlagClientSingleton();

  const flagResult = await client.getBooleanFlag(flagKey);

  return flagResult;
};
